/* eslint-disable no-new-func */
/* eslint-disable no-eval */
import {
  createException,
  RegistryErrorCode,
  createInternalException,
} from '../errors/RegistryError';
import { isServer } from '../flags';

const workaround = {} as any;

export interface IDefineArguments {
  id: string | null;
  deps: Array<string>;
  factory: any;
}
/**
 * trick to leave `require` as is after webpack build
 * it makes possible to run the same bundle on server and client
 */
eval(
  `workaround.getRequireFunction = () => typeof require !== 'undefined' ? require : undefined`,
);

export function getGlobal() {
  const currentGlobal = (
    typeof globalThis !== 'undefined'
      ? globalThis
      : typeof window !== 'undefined'
      ? window
      : typeof self !== 'undefined'
      ? self
      : typeof global !== 'undefined'
      ? global
      : null
  ) as any;

  /**
   * Temporary condition
   */
  if (currentGlobal === null) {
    console.log('');
    console.error(
      `Registry Unreachable Case – "get global variable". 
Please notify registry developers about this error.`,
    );
    console.log('');
  }

  return currentGlobal;
}

export function requireAMDModule<TResponse = any>(
  id: string,
): Promise<TResponse> {
  /**
   * This is workaround for webpack...
   * if just use `require` function directly, webpack will transpile it to
   * `__webpack_require__(n)` that always leads to module that throws exception like `module if not defined`
   */
  const localRequire = workaround.getRequireFunction();

  if (!localRequire.specified) {
    throw createInternalException(
      '"specified()" is not available from the "require" function',
    );
  }

  return new Promise<TResponse>(resolve => {
    /**
     * Trigger `require` to fix `require.specified`
     * https://github.com/requirejs/requirejs/issues/1305#issuecomment-87924865
     **/
    localRequire([]);

    /**
     * To prevent fetching module with `requirejs`
     */
    if (localRequire.specified(id)) {
      localRequire([id], (model: any) => {
        resolve(model);
      });
    } else {
      resolve(undefined as any);
    }
  });
}

const parseDefineArguments = (...args: any): IDefineArguments | null => {
  if (args.length === 3) {
    return {
      id: args[0],
      deps: args[1],
      factory: args[2],
    };
  } else if (args.length === 2) {
    return {
      id: typeof args[0] === 'string' ? args[0] : null,
      deps: Array.isArray(args[0]) ? args[0] : [],
      factory: args[1],
    };
  } else {
    return null;
  }
};

function wrapDefineFunction(params: {
  id?: string;
  define: any;
  globals?: Record<string, any>;
}) {
  /**
   * If components library was bundled with "umdNamedDefine: true", the UMD header in bundle will be:
   * ...
   * define('bundleId', [], factory)
   * ...
   *
   * If components "umdNamedDefine: false":
   * ...
   * define([], factory)
   * ...
   *
   * So this is anonymous define call, that means that it is not possible to require it after the evaluation
   * Better to track these cases.
   */
  let defineForBundle;

  if (params.define) {
    defineForBundle = (...args: any) => {
      const parsed = parseDefineArguments(...args);

      if (parsed === null) {
        throw createException(
          `Incorrect "define()" call. No arguments`,
          RegistryErrorCode.BundleEvaluationError,
        ).withHint('Probably library was bundled in a wrong way');
      }

      const { id, deps, factory } = parsed;

      if ((params.id && params.id !== id) || !id) {
        throw createException(
          `Incorrect "define()" call. Bundle IDs miss match`,
          RegistryErrorCode.BundleEvaluationError,
        )
          .withMissMatch(`${params.id}`, `${id}`)
          .withHint('Probably library was bundled in a wrong way');
      }

      const globals = params.globals;

      const filteredDeps = deps.filter(
        key => !globals || (globals && !globals[key]),
      );

      if (globals) {
        params.define(id, filteredDeps, (...values: Array<any>) => {
          const allValues = deps.map(dep => {
            const index = filteredDeps.findIndex(_dep => _dep === dep);
            if (index === -1) {
              return globals[dep];
            } else {
              return values[index];
            }
          });

          return factory(...allValues);
        });
      } else {
        params.define(...args);
      }
    };

    (defineForBundle as any).amd = params.define.amd;
  }

  return defineForBundle;
}

function getDefineFunction() {
  const currentGlobal = getGlobal();

  return typeof currentGlobal.define === 'function' && currentGlobal.define.amd
    ? currentGlobal.define
    : null;
}

export function getEnvironment({
  id,
  globals,
}: {
  id?: string;
  globals?: Record<string, any>;
}): {
  define: any;
  require?: (name: string) => any;
  module: undefined;
  exports: Record<string, any> | undefined;
  self: Record<string, any>;
} {
  const currentGlobal = getGlobal();

  const _define = getDefineFunction();

  let self = currentGlobal;

  if (globals && !_define) {
    self = new Proxy(currentGlobal, {
      get(target, key: string) {
        return key in globals ? globals[key] : target[key];
      },
    });
  }

  const currentRequire = workaround.getRequireFunction();

  return {
    /**
     * for requirejs environment
     */
    define: wrapDefineFunction({ id, define: _define, globals }),
    /**
     * `workaround.require` – always default require function
     * for SSR – it is node require
     * for CSR – it is requirejs
     */
    require: currentRequire
      ? name => {
          if (globals && globals[name]) {
            return globals[name];
          }

          return currentRequire(name);
        }
      : undefined,
    /**
     * always force `module` to be undefine during bundle evaluation
     */
    module: undefined,
    /**
     * commonjs & commonjs2
     *
     * for server env. we provide `exports` as plain object
     * after evaluation, it is possible to get the results:
     * env.exports[bundleId]
     *
     * check "typeof currentRequire !== 'undefined'" to support SSR web-worker emulation
     */
    exports: isServer && typeof currentRequire !== 'undefined' ? {} : undefined,
    /**
     *
     */
    self,
  };
}

export function evaluateBundle<TResponse = any>(
  bundle: string,
  { id, globals }: { id?: string; globals?: Record<string, any> } = {},
): Promise<TResponse> {
  const evaluateBundleSources = new Function(
    'define',
    'require',
    'module',
    'exports',
    'self',
    bundle,
  );

  const env = getEnvironment({ id, globals });

  const backup: Record<string, any> = {};
  const currentGlobal = getGlobal();

  evaluateBundleSources(
    env.define,
    env.require,
    env.module,
    env.exports,
    env.self,
  );

  Object.keys(backup).forEach(key => {
    currentGlobal[key] = backup[key];
  });
  /**
   * Nothing to return bundle id was not passed
   */
  if (!id) {
    return undefined as any;
  }

  if (typeof env.define === 'function') {
    return requireAMDModule<TResponse>(id);
  } else if (typeof env.exports === 'object') {
    env.self[id] = env.exports[id];
    return currentGlobal[id];
  } else {
    const root = typeof self !== 'undefined' ? self : currentGlobal;
    // @ts-ignore
    return root[id];
  }
}
